/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { Redirect, type Route, type Routes } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { BenchmarkRoute } from '@atlassian/twg-microsite-experience/benchmark-route';
import Home from '@atlassian/twg-microsite-experience/home';
import {
	BENCHMARK_REPORT_ROUTE_NAME,
	BENCHMARK_REPORT_ROUTE_PATH,
	BENCHMARK_ROUTE_NAME,
	BENCHMARK_ROUTE_PATH,
	BENCHMARK_REVIEW_ROUTE_NAME,
	BENCHMARK_REVIEW_ROUTE_PATH,
	BENCHMARK_RUN_ROUTE_NAME,
	BENCHMARK_RUN_ROUTE_PATH,
	CLI_ROUTE_NAME,
	CLI_ROUTE_PATH,
	MCP_ROUTE_NAME,
	MCP_ROUTE_PATH,
	CONNECT_AGENTS_ROUTE_NAME,
	CONNECT_AGENTS_ROUTE_PATH,
	HOME_ROUTE_NAME,
	HOME_ROUTE_PATH,
	type MicrositeRouteName,
} from '@atlassian/twg-microsite-experience/routes';

type MicrositeNamedRoute = {
	name: MicrositeRouteName;
	path: string;
	exact: true;
	component: Route['component'];
};

const homeRoute: MicrositeNamedRoute = {
	name: HOME_ROUTE_NAME,
	path: HOME_ROUTE_PATH,
	exact: true,
	component: Home,
};

// Lazily load the CLI page so its chunk is split out of the main bundle and is
// only fetched once `/cli` is actually routed to.
const CliPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_cli-page" */ '@atlassian/twg-microsite-experience/cli'
	).then((module) => ({ default: module.CliPage })),
);

const McpPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_mcp-page" */ '@atlassian/twg-microsite-experience/mcp'
	).then((module) => ({ default: module.McpPage })),
);

const ConnectAgentsPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_connect-agents-page" */ '@atlassian/twg-microsite-experience/connect-agents'
	).then((module) => ({ default: module.ConnectAgentsPage })),
);

/**
 * Route component for `/cli`.
 *
 * This route is deliberately ungated. `/cli` is served its own HTML document
 * (`src/cli/index.html`), which declares `robots: index, follow` and a
 * self-referencing `canonical=/cli` in the raw HTTP response, so a crawler or a
 * social unfurler treats it as an indexable page without executing any
 * JavaScript. A client-side gate cannot vary that: the gate resolver is
 * installed in the browser (`src/feature-flags`) and `hasBifrostTemplate: false`
 * leaves nothing server-side able to change the `<head>`. Gating the component
 * would therefore publish an indexable URL that redirects every real visitor to
 * `/`, so the two have to agree, and the document is the half that cannot be
 * conditional.
 */
const CliRoute = (): JSX.Element => (
	<Suspense fallback={null}>
		<CliPage />
	</Suspense>
);

const cliRoute: MicrositeNamedRoute = {
	name: CLI_ROUTE_NAME,
	path: CLI_ROUTE_PATH,
	exact: true,
	component: CliRoute,
};

const McpRoute = (): JSX.Element =>
	fg('twg_microsite_standalone_mcp_page') ? (
		<Suspense fallback={null}>
			<McpPage />
		</Suspense>
	) : (
		<Redirect to={HOME_ROUTE_PATH} />
	);

const mcpRoute: MicrositeNamedRoute = {
	name: MCP_ROUTE_NAME,
	path: MCP_ROUTE_PATH,
	exact: true,
	component: McpRoute,
};

// Which benchmark view a visitor gets (the benchmark or the public waitlist) is
// decided in `@atlassian/twg-microsite-experience/benchmark-route`. `/benchmark`
// is served its own HTML document (`src/benchmark/index.html`), so it must stay
// ungated for the same reason as `/cli` below.
const benchmarkRoute: MicrositeNamedRoute = {
	name: BENCHMARK_ROUTE_NAME,
	path: BENCHMARK_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

// A run, its review and its report render the same page, which reads the view
// it owes from the route it was matched on. All four share the home HTML
// document, and must share the one component too: starting a run navigates
// from `/benchmark` to the run while the start is in flight, and a different
// component would remount the page and lose the run being started.
const benchmarkRunRoute: MicrositeNamedRoute = {
	name: BENCHMARK_RUN_ROUTE_NAME,
	path: BENCHMARK_RUN_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

const benchmarkReviewRoute: MicrositeNamedRoute = {
	name: BENCHMARK_REVIEW_ROUTE_NAME,
	path: BENCHMARK_REVIEW_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

const benchmarkReportRoute: MicrositeNamedRoute = {
	name: BENCHMARK_REPORT_ROUTE_NAME,
	path: BENCHMARK_REPORT_ROUTE_PATH,
	exact: true,
	component: BenchmarkRoute,
};

const ConnectAgentsRoute = (): JSX.Element =>
	fg('twg_microsite_cli_mcp_landing_page') ? (
		<Suspense fallback={null}>
			<ConnectAgentsPage />
		</Suspense>
	) : (
		<Redirect to={HOME_ROUTE_PATH} />
	);

const connectAgentsRoute: MicrositeNamedRoute = {
	name: CONNECT_AGENTS_ROUTE_NAME,
	path: CONNECT_AGENTS_ROUTE_PATH,
	exact: true,
	component: ConnectAgentsRoute,
};

const catchAllRoute = {
	name: 'not-found-redirect',
	path: '*', // Matches anything not caught above
	component: () => <Redirect to="/" />, // Redirect to home
};

export const routes: Routes = [
	homeRoute,
	cliRoute,
	mcpRoute,
	benchmarkRoute,
	benchmarkRunRoute,
	benchmarkReviewRoute,
	benchmarkReportRoute,
	connectAgentsRoute,
	catchAllRoute,
] as const;
